import { toast } from '@heidi/design-system/toast';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { HEIDI_2_AWARENESS_BANNER_MODAL_ID } from '../../../contracts/heidi-2';
import { WORK_CHAT_HOME_PATH } from '../../../contracts/projects/chat-home-path';
import { getHeidi2Ports, heidi2ExploreLaterPreviewAtom } from '../../../data/heidi-2';
import { pendingWorkModeAtom } from '../../../data/projects/workMode';
import type {
  Heidi2OnboardingOutcome,
  Heidi2OnboardingStep,
} from '../../onboarding/heidi2Onboarding.types';

import type { Heidi2AwarenessBannerPorts } from './heidi-2-awareness-banner-ports';
import {
  getLoadedHeidi2OnboardingFlow,
  preloadHeidi2OnboardingFlow,
} from './heidi-2-onboarding-flow-loader';
import { heidi2OptInModalMessages } from './heidi-2-opt-in-modal.messages';

// `React.lazy` rather than `next/dynamic`: this zone may not reach for Next
// beyond `next/image`, and the tour only renders client-side anyway.
const LazyHeidi2OnboardingFlow = lazy(() =>
  preloadHeidi2OnboardingFlow().then((module) => ({
    default: module.Heidi2OnboardingFlow,
  }))
);

interface Heidi2OnboardingHostProps {
  /** The inputs typed in this zone's vocabulary, from `scribe-layout.tsx`. */
  ports: Heidi2AwarenessBannerPorts;
  /** Hide the host after the run is done and any pending activation has settled. */
  onFinished: () => void;
  /** Reopen the tour part-way in, for a clinician who chose "Explore later". */
  initialStep?: Heidi2OnboardingStep;
  /** The workspace is already on Heidi 2, so no exit from the tour activates it. */
  isActivated?: boolean;
}

/**
 * Production host for the Heidi 2.0 first-run tour. Eligibility lives in the
 * parent; this file owns activation, seen persistence, and Home landing.
 *
 * "Explore later", the close icon and Escape switch the clinician to Heidi 2
 * too — they land on Scribe rather than in the tour, and the explore-later
 * nudges pick them up there.
 * Seen state records `engage` only for a finished tour, which is how those
 * nudges tell a finisher from someone who skipped.
 */
export function Heidi2OnboardingHost({
  ports,
  onFinished,
  initialStep,
  isActivated = false,
}: Heidi2OnboardingHostProps) {
  const intl = useIntl();
  const appPorts = getHeidi2Ports();
  const navigate = appPorts.useNavigate();
  const asPath = appPorts.useRouterAsPath();
  const homeHref = appPorts.useBaseChatHref();
  const setPendingWorkHomeView = appPorts.useSetPendingWorkHomeView();
  const setPendingWorkMode = useSetAtom(pendingWorkModeAtom);
  const setExploreLaterPreview = useSetAtom(heidi2ExploreLaterPreviewAtom);
  const markModalAsViewed = appPorts.useMarkModalAsViewed();
  // Picks the hero demo written for their specialty; unknown falls to generic.
  const canonicalSpecialtyName = appPorts.useUserCanonicalSpecialtyName();
  // The hero demo shows their own EHR's mark; no connection or no bundled
  // icon falls back to a generic tile.
  const ehrLogoSrc = appPorts.useEhrLogoSrc();
  const getConsentLegalLinks = ports.getConsentLegalLinks;
  const legalLinks = useMemo(
    () => getConsentLegalLinks(intl.locale),
    [getConsentLegalLinks, intl.locale]
  );
  // Copy experiment on the welcome CTA; the getter owns the English gate.
  const welcomeCtaLabel = appPorts.useHeidi2WelcomeCtaCopy(intl.locale);
  // The finale's "Book time with an expert" only renders when a port is passed.
  const bookDemo = ports.useHeidi2BookDemoPort();
  const entryPathRef = useRef(asPath);
  const activationStatusRef = useRef<'idle' | 'pending' | 'succeeded' | 'failed'>(
    isActivated ? 'succeeded' : 'idle'
  );
  const pendingFinishRef = useRef<Heidi2OnboardingOutcome | null>(null);

  const goHome = useCallback(() => {
    if (homeHref === WORK_CHAT_HOME_PATH) {
      setPendingWorkHomeView('home');
      setPendingWorkMode('work');
    }
    navigate(homeHref);
  }, [homeHref, navigate, setPendingWorkHomeView, setPendingWorkMode]);

  const finalize = useCallback(
    (outcome: Heidi2OnboardingOutcome) => {
      markModalAsViewed({
        modal_id: HEIDI_2_AWARENESS_BANNER_MODAL_ID,
        increment_count: false,
        action:
          outcome === 'completed' ? ModalViewAction.engage : ModalViewAction.dismiss,
      });

      if (outcome === 'completed') {
        // A dev preview pins the welcome outcome, so the engage mark above
        // cannot move it; follow the finish here instead.
        setExploreLaterPreview((preview) => (preview ? 'completed' : preview));
        goHome();
      } else {
        // An existing clinician meets the takeover the moment they log in, so
        // their entry path is wherever the boot landed. Work Home is no place
        // to leave someone who just declined Heidi 2 — send them to Scribe.
        // A reopened tour is a Heidi 2 user who chose to be on Home, so it
        // keeps the page it opened over.
        const exitPath =
          initialStep === undefined && appPorts.isHomeBootPath(entryPathRef.current)
            ? ports.newSessionListPathPrefix
            : entryPathRef.current;
        if (asPath !== exitPath) navigate(exitPath);
      }

      onFinished();
    },
    [
      appPorts,
      asPath,
      goHome,
      initialStep,
      markModalAsViewed,
      navigate,
      onFinished,
      ports.newSessionListPathPrefix,
      setExploreLaterPreview,
    ]
  );

  const { mutate: activateHeidi2 } = appPorts.useMutateActivateHeidi2({
    onActivated: () => {
      activationStatusRef.current = 'succeeded';
      if (pendingFinishRef.current) {
        finalize(pendingFinishRef.current);
        pendingFinishRef.current = null;
      }
    },
    onActivationError: () => {
      activationStatusRef.current = 'failed';
      toast({
        title: intl.formatMessage(heidi2OptInModalMessages.activationError),
        tone: 'destructive',
      });
      if (pendingFinishRef.current) {
        pendingFinishRef.current = null;
        onFinished();
      }
    },
  });

  const handleAccept = useCallback(() => {
    activationStatusRef.current = 'pending';
    activateHeidi2();
  }, [activateHeidi2]);

  const handleFinish = useCallback(
    (outcome: Heidi2OnboardingOutcome) => {
      // Leaving by any route still switches them on, but the nudges that
      // follow must not wait on activation — record and close straight away.
      if (outcome !== 'completed') {
        if (initialStep === undefined && activationStatusRef.current === 'idle') {
          activationStatusRef.current = 'pending';
          activateHeidi2();
        }
        finalize(outcome);
        return;
      }

      if (activationStatusRef.current === 'pending') {
        pendingFinishRef.current = outcome;
        return;
      }

      if (activationStatusRef.current === 'failed') {
        onFinished();
        return;
      }

      finalize(outcome);
    },
    [activateHeidi2, finalize, initialStep, onFinished]
  );

  // Reopened part-way in means the banner or the checklist brought them back.
  const source = initialStep ? 'relaunch' : isActivated ? 'activated' : 'first_run';

  // Pinned for the mount: swapping lazy for eager mid-mount would remount the tour.
  const [Heidi2OnboardingFlow] = useState(
    () => getLoadedHeidi2OnboardingFlow() ?? LazyHeidi2OnboardingFlow
  );

  return (
    <Suspense fallback={null}>
      <Heidi2OnboardingFlow
        onAcceptTour={isActivated ? undefined : handleAccept}
        onFinish={handleFinish}
        {...ports.heidi2OnboardingTrackers(source, welcomeCtaLabel)}
        initialStep={initialStep}
        canonicalSpecialtyName={canonicalSpecialtyName}
        ehrLogoSrc={ehrLogoSrc}
        legalLinks={legalLinks}
        welcomeCtaLabel={welcomeCtaLabel}
        bookDemo={bookDemo}
      />
    </Suspense>
  );
}
